import './KickThingsOff.scss';

import { useState, useEffect } from 'react';
import { api } from '../../../utils/api';
import { useCurrentUser } from '../../../UserProvider';
import classNames from 'classnames';

const GENRES = [
  'Trap',
  'Trance',
  'Roots',
  'Rock',
  'Reggae',
  'R&B/Soul',
  'Rap',
  'Punk',
  'Pop',
  'New Age',
  'Metal',
  'Latin',
  'Jazz',
  'Indie',
  'Hip-hop',
  'Gospel',
  'Electronic',
  'Disco',
  'Dance',
  'Country',
  'Contemporary Blues',
  'Classical',
  'Blues',
  'Alternative',
];

const INSTRUMENTS = [
  'Winds',
  'Vocals',
  'Violin',
  'Percussion',
  'Keys',
  'Horns',
  'Electric Guitar',
  'Drums',
  'DJ',
  'Cello',
  'Beats',
  'Bass',
  'Acoustic Guitar',
  'Accordeon',
]

export default function KickThingsOff({ setCanProceed, handleNext }) {
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [address, setAddress] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [profileId, setProfileId] = useState(null);
  const [displayNameError, setDisplayNameError] = useState(false);

  const [spotifyLink, setSpotifyLink] = useState('');
  const [deezerLink, setDeezerLink] = useState('');
  const [youtubeLink, setYoutubeLink] = useState('');
  const [instagramLink, setInstagramLink] = useState('');
  const [tiktokLink, setTiktokLink] = useState('');

  const { setCurrentUser } = useCurrentUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get(`/profiles?skip=0&limit=1`);

        if (data.length === 1) {
          setProfileId(data[0].id);
          setName(data[0].name);
          setBio(data[0].bio);
          setDisplayName(data[0].display_name);
          setAddress(data[0].address);
          setSelectedGenres(data[0].genres);
          setSelectedInstruments(data[0].instruments);

          setSpotifyLink(data[0].spotify_link);
          setDeezerLink(data[0].deezer_link);
          setYoutubeLink(data[0].youtube_link);
          setInstagramLink(data[0].instagram_link);
          setTiktokLink(data[0].tiktok_link);
        }
      } catch (err) {
        console.error("Failed to fetch data", err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (profileId !== null) {
      setCanProceed(true);
    } else {
      setCanProceed(false);
    }
  }, [profileId, setCanProceed]);

  const handleCreate = async () => {
    const payload = {
      name:           name,
      bio:            bio,
      address:        address,
      genres:         selectedGenres,
      instruments:    selectedInstruments,
      spotify_link:   spotifyLink,
      deezer_link:    deezerLink,
      youtube_link:   youtubeLink,
      instagram_link: instagramLink,
      tiktok_link:    tiktokLink,
    };

    if (displayName !== null && displayName.trim() !== "") {
      payload.display_name = displayName;
    } else {
      setDisplayNameError(true);
      return;
    }

    try {
      setDisplayNameError(false);
      if (profileId !== null) {
        await api.put(`/profiles/${profileId}`, payload);
        handleNext();
      } else {
        const {data} = await api.post(`/profiles`, payload);
        setProfileId(data.id);
        setCurrentUser(prev => ({ ...prev, display_name: displayName }));
        handleNext();
      }
    } catch (err) {
      if (err.response && err.response.status === 400) {
        setDisplayNameError(true);
      }
    }
  };

  const toggleGenre = (genre) => {
    setSelectedGenres((prev) => {
      const alreadySelected = prev.includes(genre);

      // Remove the genre from the selection if already selected
      if (alreadySelected) {
        return prev.filter((g) => g !== genre);
      }

      // Do not allow more than 3 selections
      if (prev.length >= 3) {
        return prev;
      }

      // Add the genre to the selection
      return [...prev, genre];
    });
  };

  const toggleInstrument = (instrument) => {
    setSelectedInstruments((prev) => {
      const alreadySelected = prev.includes(instrument);

      // Remove the instrument from the selection if already selected
      if (alreadySelected) {
        return prev.filter((i) => i !== instrument);
      }

      // Add the instrument to the selection
      return [...prev, instrument];
    });
  };

  const renderSocialInput = (logo, placeholder, value, setter) => (
    <div className="social">
      <img className="social-logo" src={`/images/${logo}`} alt="" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setter(e.target.value)}
      />
    </div>
  );

  return (
    <div className="__kick_things_off">
      <div className="title">LET'S KICK THINGS OFF</div>
      <div className="sub-title">Time to build your unique profile! Add the information with your artistic personality. Share your next concerts, latest releases, playlists, and your tip jar. Papaya will generate your unique profile QR code.</div>
      <div className="sub-title">Sharing your art has never been this easy!</div>
      <div className="sub-title">Friendly tip: the more information you add, the more complete and wide your digital footprint will be.</div>
      <input
        type="text"
        placeholder="Type your artist name here"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <div className={classNames("fade", "error", {show: displayNameError})}>
        Profile link already taken or invalid
      </div>
      <input
        type="text"
        placeholder="Unique profile link"
        value={displayName}
        onChange={(e) => {
          const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9-_]/g, '');
          setDisplayName(sanitizedValue);
        }}
      />
      <textarea
        type="text"
        placeholder="Give us a short bio of who you are"
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        maxLength={250}
        rows={4}
      />
      <div className="bio-count">{bio.length}/250</div>
      <input
        type="text"
        placeholder="Location"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />
      <div className="category">What is your match?</div>
      <div className="category-description">Choose up to 3 genres that represent your style</div>
      <div className="genre-grid">
        {GENRES.map((genre) => (
          <div
            key={genre}
            className={classNames("genre", {selected: selectedGenres.includes(genre)})}
            onClick={() => toggleGenre(genre)}
          >
            {genre}
          </div>
        ))}
      </div>

      <div className="category">What are you best known for?</div>
      <div className="category-description">Choose up to 3 instruments</div>
      <div className="genre-grid">
        {INSTRUMENTS.map((instrument) => (
          <div
            key={instrument}
            className={classNames("genre", {selected: selectedInstruments.includes(instrument)})}
            onClick={() => toggleInstrument(instrument)}
          >
            {instrument}
          </div>
        ))}
      </div>

      <div className="category">Link up your socials</div>
      <div className="category-description">Share your music with the world, all in one place.</div>

      { renderSocialInput("spotify_logo.png", "Spotify username", spotifyLink, setSpotifyLink) }
      { renderSocialInput("deezer_logo.svg", "Deezer username", deezerLink, setDeezerLink) }
      { renderSocialInput("youtube_logo.png", "YouTube username", youtubeLink, setYoutubeLink) }
      { renderSocialInput("instagram_logo.png", "Instagram username", instagramLink, setInstagramLink) }
      { renderSocialInput("tiktok_logo.png", "TikTok username", tiktokLink, setTiktokLink) }

      <button className="connect ppy-btn ppy-btn-primary" onClick={handleCreate}>
        Create My Papaya Profile
      </button>
    </div>
  );
}